import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/api";
import { formatearFecha, fechaISO, fechaLocal } from "../utils/fechas";

const font = "Montserrat, sans-serif";

const STATUS_CONFIG = {
  submitted: { label: "Enviado",   color: "#b06a00", bg: "rgba(176,106,0,0.08)",  icon: "📤" },
  viewed:    { label: "Visto",     color: "#163244", bg: "rgba(22,50,68,0.07)",   icon: "👁" },
  accepted:  { label: "Aceptado",  color: "#1b7f4e", bg: "rgba(27,127,78,0.08)", icon: "✓" },
  rejected:  { label: "Rechazado", color: "#c0392b", bg: "rgba(192,57,43,0.08)", icon: "✕" },
  expired:   { label: "Expirado",  color: "#7b5ea7", bg: "rgba(123,94,167,0.08)",icon: "⏰" },
};

const SERVICE_LABELS = {
  plomeria: "Plomería", electricidad: "Electricidad", gasista: "Gasista",
  pintura: "Pintura", albanileria: "Albañilería", cerrajeria: "Cerrajería",
  limpieza: "Limpieza", jardineria: "Jardinería",
  aire_acondicionado: "Aire Acondicionado", arquitectura: "Arquitectura",
  belleza_personal: "Belleza Personal", carpinteria: "Carpintería",
  control_plagas: "Control de Plagas", cuidado_mayores: "Cuidado de Mayores",
  cuidado_ninos: "Cuidado de Niños", decoradores: "Decoradores",
  electricista: "Electricista", entrenamiento: "Entrenamiento",
  herrero: "Herrero", infantiles: "Infantiles", instalador_general: "Instalador General",
  jardinero: "Jardinero", mascotas: "Mascotas", mecanica_automotriz: "Mecánica Automotriz",
  mudanzas: "Mudanzas", piletas: "Piletas", pintores: "Pintores",
  plomeros: "Plomeros", seguridad: "Seguridad", servicio_domestico: "Servicio Doméstico",
  tapicero: "Tapicero", tecnologia: "Tecnología",
  // kebab-case variants
  "plomeros": "Plomeros", "electricista": "Electricista",
};

function formatARS(amount) {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(amount);
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "hace un momento";
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

function remainingTime(dateStr) {
  const diff = new Date(dateStr) - Date.now();
  if (diff <= 0) return null;
  const hrs  = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return hrs > 0 ? `${hrs}h ${mins}min` : `${mins}min`;
}

// ─── Toggle switch reutilizable ───────────────────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
      <div style={{ width: 44, height: 24, borderRadius: 12, background: value ? "#163244" : "#c8d4dc", position: "relative", transition: "background 0.25s" }}>
        <div style={{ position: "absolute", top: 3, left: value ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: value ? "#E7A432" : "#fff", transition: "left 0.25s" }} />
      </div>
      <span style={{ fontSize: 13, color: "#163244", fontWeight: 600, fontFamily: font }}>{value ? "Sí" : "No"}</span>
    </div>
  );
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────
function EditModal({ quote, onSave, onClose }) {
  const [form, setForm] = useState({
    amount:                   quote.amount,
    description:              quote.description,
    estimated_visit_required: quote.estimated_visit_required,
    estimated_start_date:     fechaISO(quote.estimated_start_date),
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.patch(`/matching/provider/quotes/${quote.id}`, {
        amount:                   form.amount,
        description:              form.description,
        estimated_visit_required: form.estimated_visit_required,
        estimated_start_date:     form.estimated_start_date || null,
      });
      onSave({ ...quote, ...form });
      onClose();
    } catch (err) {
      console.error("Error al actualizar presupuesto:", err);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { padding: "10px 14px", borderRadius: 9, border: "1.5px solid rgba(0,0,0,0.12)", fontSize: 14, fontFamily: font, color: "#163244", background: "#fff", outline: "none", width: "100%", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, fontFamily: font };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 18, padding: "28px", width: "min(520px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 16px 60px rgba(22,50,68,0.22)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <h3 style={{ margin: 0, color: "#163244", fontSize: 17, fontWeight: 800, fontFamily: font }}>Editar presupuesto</h3>
          <button onClick={onClose} style={{ background: "rgba(0,0,0,0.05)", border: "none", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, color: "#7a93a4" }}>✕</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Monto (ARS) *</label>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14, fontWeight: 700, color: "#163244", pointerEvents: "none" }}>$</span>
            <input type="text" inputMode="numeric" style={{ ...inputStyle, paddingLeft: 26 }}
              value={form.amount === 0 || form.amount === "" ? "" : new Intl.NumberFormat("es-AR").format(form.amount)}
              onChange={e => { const raw = e.target.value.replace(/\./g, "").replace(/[^0-9]/g, ""); setForm(f => ({ ...f, amount: raw === "" ? "" : Number(raw) })); }}
              placeholder="0" />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Descripción del trabajo *</label>
          <textarea style={{ ...inputStyle, minHeight: 100, resize: "vertical", lineHeight: 1.6 }}
            value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>

        <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Inicio estimado</label>
            <input type="date" style={inputStyle} value={form.estimated_start_date}
              onChange={e => setForm(f => ({ ...f, estimated_start_date: e.target.value }))} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ ...labelStyle, marginBottom: 10 }}>¿Requiere visita?</label>
            <Toggle value={form.estimated_visit_required} onChange={v => setForm(f => ({ ...f, estimated_visit_required: v }))} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "11px 22px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "11px 28px", borderRadius: 50, background: "#163244", border: "none", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8 }}>
            {loading ? <span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Quote Modal ──────────────────────────────────────────────────────────
function NewQuoteModal({ onSave, onClose, preselectedRequestId = null, preselectedLabel = null }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ request_id: preselectedRequestId || "", amount: "", description: "", estimated_visit_required: false, estimated_start_date: "" });
  const [pendingRequests,  setPendingRequests]  = useState([]);
  const [loadingRequests,  setLoadingRequests]  = useState(!preselectedRequestId);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState("");

  useEffect(() => {
    if (preselectedRequestId) return;
    setLoadingRequests(true);
    api.get("/matching/provider/requests")
      .then(res => {
        const quoteRequests = (res.data.requests || []).filter(r => r.request_type === "quote" && ["notified","accepted"].includes(r.response_status));
        setPendingRequests(quoteRequests);
        if (quoteRequests.length === 1) setForm(f => ({ ...f, request_id: quoteRequests[0].request_id }));
      })
      .catch(() => setPendingRequests([]))
      .finally(() => setLoadingRequests(false));
  }, [preselectedRequestId]);

  const handleSave = async () => {
    if (!form.amount || !form.description || !form.request_id) { setError("Completá todos los campos requeridos."); return; }
    setError(""); setLoading(true);
    try {
      const res = await api.post("/matching/provider/quotes", {
        request_id: Number(form.request_id), amount: Number(form.amount),
        description: form.description, estimated_visit_required: form.estimated_visit_required,
        estimated_start_date: form.estimated_start_date || null,
      });
      onSave(res.data.quote || res.data);
      onClose();
      navigate("/prestador/solicitudes");
    } catch (err) {
      setError(err.response?.data?.error || "Error al enviar el presupuesto.");
    } finally { setLoading(false); }
  };

  const inputStyle = { padding: "10px 14px", borderRadius: 9, border: "1.5px solid rgba(0,0,0,0.12)", fontSize: 14, fontFamily: font, color: "#163244", background: "#fff", outline: "none", width: "100%", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, fontFamily: font };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 18, padding: "28px", width: "min(520px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 16px 60px rgba(22,50,68,0.22)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <h3 style={{ margin: 0, color: "#163244", fontSize: 17, fontWeight: 800, fontFamily: font }}>Enviar presupuesto</h3>
          <button onClick={onClose} style={{ background: "rgba(0,0,0,0.05)", border: "none", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, color: "#7a93a4" }}>✕</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Solicitud *</label>
          {preselectedRequestId ? (
            <div style={{ padding: "10px 14px", borderRadius: 9, background: "rgba(22,50,68,0.05)", fontSize: 14, color: "#163244", fontWeight: 600, border: "1.5px solid rgba(22,50,68,0.12)", display: "flex", alignItems: "center", gap: 8 }}>
              ✓ {preselectedLabel || `Solicitud #${preselectedRequestId}`}
            </div>
          ) : loadingRequests ? (
            <div style={{ color: "#7a93a4", fontSize: 13, padding: "10px 0" }}>Cargando solicitudes...</div>
          ) : pendingRequests.length === 0 ? (
            <div style={{ padding: "12px 14px", borderRadius: 9, background: "#f5f7fa", fontSize: 13, color: "#7a93a4", border: "1.5px solid rgba(0,0,0,0.08)" }}>No hay solicitudes de presupuesto pendientes.</div>
          ) : (
            <select style={{ ...inputStyle, cursor: "pointer" }} value={form.request_id} onChange={e => setForm(f => ({ ...f, request_id: e.target.value }))}>
              <option value="">Seleccioná una solicitud...</option>
              {pendingRequests.map(r => (
                <option key={r.match_id} value={r.request_id}>#{r.request_id} — {SERVICE_LABELS[r.service_key] || r.service_key} — {r.client_name}</option>
              ))}
            </select>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Monto (ARS) *</label>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14, fontWeight: 700, color: "#163244", pointerEvents: "none" }}>$</span>
            <input type="text" inputMode="numeric" style={{ ...inputStyle, paddingLeft: 26 }}
              value={form.amount === "" ? "" : new Intl.NumberFormat("es-AR").format(form.amount)}
              onChange={e => { const raw = e.target.value.replace(/\./g, "").replace(/[^0-9]/g, ""); setForm(f => ({ ...f, amount: raw === "" ? "" : raw })); }}
              placeholder="0" />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Descripción del trabajo *</label>
          <textarea style={{ ...inputStyle, minHeight: 90, resize: "vertical", lineHeight: 1.6 }}
            placeholder="Describí el trabajo incluido en el presupuesto..."
            value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>

        <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Inicio estimado</label>
            <input type="date" style={inputStyle} value={form.estimated_start_date} onChange={e => setForm(f => ({ ...f, estimated_start_date: e.target.value }))} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ ...labelStyle, marginBottom: 10 }}>¿Requiere visita?</label>
            <Toggle value={form.estimated_visit_required} onChange={v => setForm(f => ({ ...f, estimated_visit_required: v }))} />
          </div>
        </div>

        {error && <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 9, background: "rgba(192,57,43,0.08)", color: "#c0392b", fontSize: 13, fontWeight: 600 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "11px 22px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <button onClick={handleSave} disabled={loading || !form.amount || !form.description || !form.request_id}
            style={{ padding: "11px 28px", borderRadius: 50, background: "#163244", border: "none", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: (loading || !form.amount || !form.request_id) ? "not-allowed" : "pointer", opacity: (!form.amount || !form.description || !form.request_id) ? 0.6 : 1, display: "flex", alignItems: "center", gap: 8 }}>
            {loading ? <span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> : "Enviar presupuesto"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Quote Card ───────────────────────────────────────────────────────────────
function QuoteCard({ quote, onEdit, onChat }) {
  const cfg      = STATUS_CONFIG[quote.status] || STATUS_CONFIG.submitted;
  const canEdit  = quote.status === "submitted";
  const isAcc    = quote.status === "accepted";
  const isDim    = ["rejected","expired"].includes(quote.status);
  const remaining = remainingTime(quote.valid_until);
  const label    = SERVICE_LABELS[quote.service_key] || quote.service_key;

  return (
    <div style={{
      background: "#fff", borderRadius: 16, marginBottom: 12, overflow: "hidden",
      border: `1.5px solid ${isAcc ? "#1b7f4e" : "rgba(0,0,0,0.08)"}`,
      boxShadow: isAcc ? "0 4px 20px rgba(27,127,78,0.1)" : "0 2px 8px rgba(22,50,68,0.05)",
      opacity: isDim ? 0.6 : 1, transition: "all 0.2s",
    }}>
      {/* Strip de estado */}
      <div style={{ background: cfg.bg, padding: "6px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${cfg.color}1a` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontSize: 13 }}>{cfg.icon}</span>
          <span style={{ fontSize: 10, fontWeight: 800, color: cfg.color, letterSpacing: "0.06em" }}>{cfg.label.toUpperCase()}</span>
        </div>
        {canEdit && remaining && <span style={{ fontSize: 11, color: "#b06a00", fontWeight: 600 }}>Válido {remaining}</span>}
        {canEdit && !remaining && <span style={{ fontSize: 11, color: "#c0392b", fontWeight: 600 }}>Expiró</span>}
      </div>

      <div style={{ padding: "16px 18px" }}>
        {/* Top row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: "#163244", marginBottom: 3 }}>{quote.client_name}</div>
            <div style={{ fontSize: 12, color: "#7a93a4" }}>
              {label}{quote.common_data?.address && <> · 📍 {quote.common_data.address}</>}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: isAcc ? "#1b7f4e" : "#163244", lineHeight: 1 }}>{formatARS(parseFloat(quote.amount))}</div>
            <div style={{ fontSize: 11, color: "#aab8c2", marginTop: 2 }}>Enviado {timeAgo(quote.created_at)}</div>
          </div>
        </div>

        {/* Descripción */}
        <div style={{ background: "#f8fafb", borderRadius: 9, padding: "10px 13px", fontSize: 13, color: "#163244", lineHeight: 1.65, borderLeft: "3px solid rgba(22,50,68,0.12)", marginBottom: 12 }}>
          {quote.description}
        </div>

        {/* Chips */}
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 14 }}>
          {quote.estimated_start_date && (
            <div style={{ background: "#f0f5f8", borderRadius: 7, padding: "4px 11px", fontSize: 11, color: "#163244", display: "flex", gap: 5, alignItems: "center" }}>
              📅 <strong>{formatearFecha(quote.estimated_start_date)}</strong>
            </div>
          )}
          <div style={{ background: quote.estimated_visit_required ? "rgba(176,106,0,0.08)" : "rgba(27,127,78,0.08)", borderRadius: 7, padding: "4px 11px", fontSize: 11, color: quote.estimated_visit_required ? "#b06a00" : "#1b7f4e" }}>
            {quote.estimated_visit_required ? "🔍 Requiere visita" : "✓ Sin visita previa"}
          </div>
        </div>

        {/* Acción editar */}
        {canEdit && (
          <button onClick={() => onEdit(quote)} style={{ padding: "9px 18px", borderRadius: 50, border: "1.5px solid #163244", background: "transparent", color: "#163244", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
            ✏ Editar presupuesto
          </button>
        )}

        {/* Aceptado — contacto + chat */}
        {isAcc && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ background: "rgba(27,127,78,0.07)", border: "1px solid rgba(27,127,78,0.2)", borderRadius: 9, padding: "10px 14px", fontSize: 13, color: "#1b7f4e", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
              🎉 ¡El cliente aceptó tu presupuesto! Coordiná el trabajo.
            </div>
            {(quote.client_phone || quote.client_email) && (
              <div style={{ background: "#f5f7fa", borderRadius: 9, padding: "12px 14px", border: "1px solid rgba(0,0,0,0.07)" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Contacto del cliente</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span>👤</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#163244" }}>{quote.client_name}</span>
                  </div>
                  {quote.client_phone && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span>📱</span>
                      <a href={`tel:${quote.client_phone}`} style={{ fontSize: 13, color: "#163244", fontWeight: 600, textDecoration: "none" }}>{quote.client_phone}</a>
                    </div>
                  )}
                  {quote.client_email && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span>✉️</span>
                      <a href={`mailto:${quote.client_email}`} style={{ fontSize: 13, color: "#163244", fontWeight: 600, textDecoration: "none" }}>{quote.client_email}</a>
                    </div>
                  )}
                </div>
              </div>
            )}
            <button onClick={() => onChat(quote.request_id)} style={{ padding: "10px", borderRadius: 50, background: "#E7A432", border: "none", color: "#163244", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              💬 Abrir chat con el cliente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Agenda constants ─────────────────────────────────────────────────────────
const DAYS_ES    = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];
const DAYS_SHORT = ["D","L","M","X","J","V","S"];
const MONTHS_ES  = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const HOURS      = [8,9,10,11,12,13,14,15,16,17,18];

const SERVICE_ICONS = {
  plomeros: "🔧", plomeria: "🔧", electricista: "⚡", electricidad: "⚡",
  gasista: "🔥", pintores: "🖌️", pintura: "🖌️", albanileria: "🧱",
  cerrajeria: "🔑", limpieza: "🧺", servicio_domestico: "🧺",
  jardineria: "🌿", jardinero: "🌿", aire_acondicionado: "❄️",
  arquitectura: "🏛️", belleza_personal: "💇", carpinteria: "🪵",
  control_plagas: "🐛", cuidado_mayores: "👴", cuidado_ninos: "👶",
  decoradores: "🎨", entrenamiento: "💪", herrero: "⚒️",
  infantiles: "🎉", instalador_general: "🔩", mascotas: "🐾",
  mecanica_automotriz: "🚗", mudanzas: "📦", piletas: "🏊",
  seguridad: "🔒", tapicero: "🛋️", tecnologia: "💻",
};

const JOB_STATUS_COLOR = {
  scheduled:   { bg: "rgba(22,50,68,0.08)",   border: "#163244", text: "#163244" },
  in_progress: { bg: "rgba(231,164,50,0.12)", border: "#E7A432", text: "#b8820d" },
  completed:   { bg: "rgba(27,127,78,0.08)",  border: "#1b7f4e", text: "#1b7f4e" },
};

const JOB_STATUS_LABEL = {
  scheduled: "Programado", in_progress: "En progreso", completed: "Completado",
};

function buildJobEvents(jobs) {
  return jobs
    .filter(j => ["scheduled","in_progress","completed"].includes(j.status) && j.scheduled_start)
    .map(j => {
      // "Todo el día" es una fecha de calendario: no se convierte de zona
      const date  = j.all_day ? fechaLocal(j.scheduled_start) : new Date(j.scheduled_start);
      const color = JOB_STATUS_COLOR[j.status] || JOB_STATUS_COLOR.scheduled;
      return {
        id: j.id, title: SERVICE_LABELS[j.service_key] || j.service_key,
        icon: SERVICE_ICONS[j.service_key] || "🛠️",
        date, hour: date.getHours() || 9, duration: j.estimated_duration_hs || 2,
        status: j.status, client: j.client_name || "", color,
      };
    });
}

function AgendaWidget({ jobs }) {
  const [view,    setView]    = useState("month");
  const [current, setCurrent] = useState(new Date());

  const events   = useMemo(() => buildJobEvents(jobs), [jobs]);
  const today    = new Date();
  const isToday  = (d) => d.toDateString() === today.toDateString();
  const eventsOn = (date) => events.filter(e => e.date.toDateString() === date.toDateString());

  const navPeriod = (dir) => {
    const d = new Date(current);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir);
    setCurrent(d);
  };

  const periodLabel = () => {
    if (view === "month") return `${MONTHS_ES[current.getMonth()]} ${current.getFullYear()}`;
    return `${DAYS_ES[current.getDay()]} ${current.getDate()} ${MONTHS_ES[current.getMonth()]}`;
  };

  const MonthView = () => {
    const y = current.getFullYear(), m = current.getMonth();
    const firstDay    = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push({ date: new Date(y, m, -firstDay + i + 1), other: true });
    for (let i = 1; i <= daysInMonth; i++) cells.push({ date: new Date(y, m, i), other: false });
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      cells.push({ date: new Date(last.getTime() + 86400000), other: true });
    }
    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", borderBottom: "1px solid rgba(0,0,0,0.05)", padding: "0 6px" }}>
          {DAYS_SHORT.map(d => <div key={d} style={{ padding: "7px 0", textAlign: "center", fontSize: 10, fontWeight: 700, color: "#aab8c2" }}>{d}</div>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", padding: "4px 6px 6px" }}>
          {cells.map(({ date, other }, i) => {
            const evs = eventsOn(date), hasEv = evs.length > 0;
            return (
              <div key={i} onClick={() => { if (!other && hasEv) { setCurrent(date); setView("day"); } }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "3px 0", opacity: other ? 0.3 : 1, cursor: !other && hasEv ? "pointer" : "default" }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: isToday(date) ? "#163244" : hasEv && !other ? "#E7A432" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: isToday(date) || hasEv ? 800 : 400, color: isToday(date) ? "#fff" : hasEv && !other ? "#163244" : "#5a6e7f" }}>
                  {date.getDate()}
                </div>
                {hasEv && !other && !isToday(date) && <div style={{ width: 4, height: 4, borderRadius: "50%", background: evs[0].color.border, marginTop: 1 }} />}
              </div>
            );
          })}
        </div>
        {events.filter(e => e.date.getMonth() === current.getMonth() && e.date.getFullYear() === current.getFullYear())
          .sort((a, b) => a.date - b.date).slice(0, 3).map((ev, i) => (
          <div key={i} onClick={() => { setCurrent(ev.date); setView("day"); }}
            style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 10px", borderTop: "1px solid rgba(0,0,0,0.05)", cursor: "pointer" }}
            onMouseEnter={e => e.currentTarget.style.background = "#f5f7fa"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ width: 3, alignSelf: "stretch", borderRadius: 2, background: ev.color.border, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#163244", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.icon} {ev.title}</div>
              <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 1 }}>{ev.date.getDate()} {MONTHS_ES[ev.date.getMonth()].slice(0,3)} · {ev.hour}:00{ev.client && ` · ${ev.client}`}</div>
            </div>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#aab8c2" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
          </div>
        ))}
      </div>
    );
  };

  const DayView = () => {
    const evs = eventsOn(current), SLOT_H = 44;
    return (
      <div>
        <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(0,0,0,0.05)", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: isToday(current) ? "#163244" : "#f0f5f8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: isToday(current) ? "#E7A432" : "#163244" }}>{current.getDate()}</span>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#163244" }}>{DAYS_ES[current.getDay()]}, {MONTHS_ES[current.getMonth()].slice(0,3)} {current.getFullYear()}</div>
            <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 1 }}>{evs.length === 0 ? "Sin trabajos" : `${evs.length} trabajo${evs.length !== 1 ? "s" : ""}`}</div>
          </div>
        </div>
        {evs.length === 0 ? (
          <div style={{ padding: "24px 14px", textAlign: "center", color: "#aab8c2" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 6 }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <div style={{ fontSize: 11, fontWeight: 600 }}>Sin trabajos este día</div>
          </div>
        ) : (
          <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
            {evs.map((ev, j) => {
              const c = ev.color;
              return (
                <div key={j} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: c.text, marginBottom: 4 }}>{ev.icon} {ev.title}</div>
                  <div style={{ fontSize: 11, color: c.text, opacity: 0.75, marginBottom: ev.client ? 4 : 6 }}>{ev.hour}:00 – {ev.hour + ev.duration}:00</div>
                  {ev.client && <div style={{ fontSize: 11, color: c.text, opacity: 0.7, marginBottom: 6 }}>👤 {ev.client}</div>}
                  <div style={{ display: "inline-flex", background: "rgba(255,255,255,0.55)", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700, color: c.text }}>{JOB_STATUS_LABEL[ev.status] || ev.status}</div>
                </div>
              );
            })}
          </div>
        )}
        {evs.length > 0 && (
          <div style={{ padding: "0 10px 8px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "36px 1fr", borderRadius: 8, overflow: "hidden", border: "1px solid rgba(0,0,0,0.06)" }}>
              <div style={{ borderRight: "1px solid rgba(0,0,0,0.05)", background: "#fafbfc" }}>
                {HOURS.map(h => <div key={h} style={{ height: SLOT_H, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 5, fontSize: 9, color: "#bcc8d0", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>{h}</div>)}
              </div>
              <div style={{ position: "relative" }}>
                {HOURS.map(h => <div key={h} style={{ height: SLOT_H, borderBottom: "1px solid rgba(0,0,0,0.04)" }} />)}
                {evs.map((ev, j) => {
                  const top = (ev.hour - HOURS[0]) * SLOT_H + 3, height = Math.max(ev.duration * SLOT_H - 6, SLOT_H - 6), c = ev.color;
                  return <div key={j} style={{ position: "absolute", left: 3, right: 3, top, height, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 6, padding: "3px 6px", overflow: "hidden" }}><div style={{ fontSize: 10, fontWeight: 700, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.icon} {ev.title}</div></div>;
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden", fontFamily: font }}>
      <div style={{ padding: "10px 12px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 6 }}>
        <button onClick={() => navPeriod(-1)} style={{ width: 26, height: 26, borderRadius: 7, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", flexShrink: 0 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#163244", flex: 1, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{periodLabel()}</span>
        <button onClick={() => navPeriod(1)} style={{ width: 26, height: 26, borderRadius: 7, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", flexShrink: 0 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
        </button>
        <button onClick={() => setCurrent(new Date())} style={{ padding: "4px 9px", borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: "pointer", fontSize: 10, fontWeight: 700, color: "#163244", fontFamily: font, flexShrink: 0 }}>Hoy</button>
        <div style={{ display: "flex", background: "#f5f7fa", borderRadius: 7, padding: 2, gap: 1, flexShrink: 0 }}>
          {[["month","Mes"],["day","Día"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setView(v)} style={{ padding: "4px 8px", borderRadius: 5, border: "none", background: view === v ? "#163244" : "transparent", color: view === v ? "#fff" : "#7a93a4", fontFamily: font, fontWeight: 700, fontSize: 10, cursor: "pointer", transition: "all 0.15s" }}>{lbl}</button>
          ))}
        </div>
      </div>
      {view === "month" && <MonthView />}
      {view === "day"   && <DayView />}
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ quotes, jobs, onNewQuote }) {
  const [agendaOpen, setAgendaOpen] = useState(true);
  const scheduledCount = jobs.filter(j => ["scheduled","in_progress"].includes(j.status) && j.scheduled_start).length;

  const now        = new Date();
  const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const accepted   = quotes.filter(q => q.status === "accepted");
  const decided    = quotes.filter(q => ["accepted","rejected"].includes(q.status)).length;
  const convRate   = decided > 0 ? Math.round((accepted.length / decided) * 100) : 0;
  const totalARS       = accepted.reduce((s, q) => s + parseFloat(q.amount || 0), 0);
  const lastMonthARS   = accepted.filter(q => new Date(q.created_at) >= startMonth).reduce((s, q) => s + parseFloat(q.amount || 0), 0);
  const lastMonthCount = accepted.filter(q => new Date(q.created_at) >= startMonth).length;

  const svcCount = {};
  accepted.forEach(q => { const lbl = SERVICE_LABELS[q.service_key] || q.service_key; svcCount[lbl] = (svcCount[lbl] || 0) + 1; });
  const topSvcs   = Object.entries(svcCount).sort((a, b) => b[1] - a[1]);
  const svcColors = ["#163244","#E7A432","#1b7f4e","rgba(22,50,68,0.4)","rgba(231,164,50,0.5)"];
  const svcTotal  = topSvcs.reduce((s, [, c]) => s + c, 0);

  const recent = [...quotes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 4)
    .map(q => ({ label: q.client_name, sub: SERVICE_LABELS[q.service_key] || q.service_key, status: STATUS_CONFIG[q.status]?.label || q.status, color: STATUS_CONFIG[q.status]?.color || "#7a93a4", time: timeAgo(q.created_at) }));

  const sCard = { background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 14, fontFamily: font };
  const sHdr  = { padding: "14px 16px 0", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 };
  const sAcc  = { width: 3, height: 13, background: "#E7A432", borderRadius: 2, flexShrink: 0 };
  const sTit  = { fontSize: 11, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: 1 };

  return (
    <div style={{ fontFamily: font }}>

      {/* 1 — CTA nuevo presupuesto */}
      <div style={{ background: "#163244", borderRadius: 14, overflow: "hidden", marginBottom: 14, position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 85% 15%, rgba(231,164,50,0.18) 0%, transparent 55%), repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.015) 40px, rgba(255,255,255,0.015) 41px)", pointerEvents: "none" }} />
        <div style={{ padding: "18px 18px 20px", position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Nuevo trabajo</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Enviá un presupuesto</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", lineHeight: 1.5, marginBottom: 16 }}>Respondé las solicitudes pendientes antes de que expiren.</div>
          <button onClick={onNewQuote} style={{ width: "100%", background: "#E7A432", color: "#163244", border: "none", borderRadius: 50, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: font }}>
            + Nuevo presupuesto
          </button>
        </div>
      </div>

      {/* 2 — Agenda colapsable */}
      <div style={{ ...sCard, marginBottom: 14 }}>
        <button onClick={() => setAgendaOpen(o => !o)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent", border: "none", padding: "14px 16px", cursor: "pointer", fontFamily: font, borderBottom: agendaOpen ? "1px solid rgba(0,0,0,0.06)" : "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={sAcc}/>
            <span style={sTit}>Mi agenda</span>
            {scheduledCount > 0 && <span style={{ background: "#E7A432", color: "#163244", borderRadius: 50, padding: "1px 8px", fontSize: 10, fontWeight: 800 }}>{scheduledCount}</span>}
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7a93a4" strokeWidth="2.5" strokeLinecap="round" style={{ transform: agendaOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}>
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
        {agendaOpen && <AgendaWidget jobs={jobs} />}
      </div>

      {/* 3 — Facturación */}
      <div style={sCard}>
        <div style={sHdr}><div style={sAcc}/><span style={sTit}>Facturación</span></div>
        <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ background: "rgba(27,127,78,0.07)", borderRadius: 10, padding: "13px 14px", border: "1px solid rgba(27,127,78,0.15)" }}>
            <div style={{ fontSize: 10, color: "#7a93a4", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Este mes · {now.toLocaleString("es-AR", { month: "long", year: "numeric" })}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#1b7f4e", lineHeight: 1, marginBottom: 3 }}>
              {lastMonthARS > 0 ? formatARS(lastMonthARS) : <span style={{ color: "#aab8c2", fontSize: 15 }}>Sin ingresos aún</span>}
            </div>
            {lastMonthCount > 0 && <div style={{ fontSize: 11, color: "#1b7f4e", opacity: 0.75 }}>{lastMonthCount} trabajo{lastMonthCount !== 1 ? "s" : ""} facturado{lastMonthCount !== 1 ? "s" : ""}</div>}
          </div>
          <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "13px 14px" }}>
            <div style={{ fontSize: 10, color: "#7a93a4", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Total histórico</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#163244", lineHeight: 1, marginBottom: 3 }}>
              {totalARS > 0 ? formatARS(totalARS) : <span style={{ color: "#aab8c2", fontSize: 15 }}>Sin ingresos aún</span>}
            </div>
            {accepted.length > 0 && <div style={{ fontSize: 11, color: "#7a93a4" }}>{accepted.length} trabajo{accepted.length !== 1 ? "s" : ""} en total</div>}
          </div>
        </div>
      </div>

      {/* 4 — Por categoría */}
      {topSvcs.length > 0 && (
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Por categoría</span></div>
          <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
            {topSvcs.map(([lbl, cnt], i) => {
              const pct = svcTotal > 0 ? Math.round((cnt / svcTotal) * 100) : 0;
              return (
                <div key={lbl} style={{ display: "flex", flexDirection: "column", gap: 4, padding: "6px 0", borderBottom: i < topSvcs.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: svcColors[i] || "#ccc" }} />
                    <span style={{ fontSize: 12, color: "#5a6e7f", flex: 1 }}>{lbl}</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#163244" }}>{cnt}</span>
                    <span style={{ fontSize: 10, color: "#aab8c2", minWidth: 28, textAlign: "right" }}>{pct}%</span>
                  </div>
                  <div style={{ height: 3, borderRadius: 2, background: "#e8eef2", overflow: "hidden", marginLeft: 16 }}>
                    <div style={{ height: "100%", borderRadius: 2, background: svcColors[i] || "#ccc", width: `${pct}%`, transition: "width 0.5s" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 5 — Conversión */}
      {decided > 0 && (
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Conversión</span></div>
          <div style={{ padding: "0 16px 16px" }}>
            <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: "#7a93a4", fontWeight: 500 }}>Aceptados sobre decididos</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: convRate >= 40 ? "#1b7f4e" : "#b06a00", marginLeft: 10, flexShrink: 0 }}>{convRate}%</span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: "#e0e9ef", overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 2, background: convRate >= 40 ? "#1b7f4e" : "#E7A432", width: `${convRate}%`, transition: "width 0.5s" }} />
              </div>
              <div style={{ fontSize: 10, color: "#aab8c2", marginTop: 6 }}>{accepted.length} aceptados de {decided} con respuesta</div>
            </div>
          </div>
        </div>
      )}

      {/* Actividad reciente */}
      {recent.length > 0 && (
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Actividad reciente</span></div>
          <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column" }}>
            {recent.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: i < recent.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: a.color, flexShrink: 0, marginTop: 4 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: "#163244", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.label}</div>
                  <div style={{ fontSize: 11, color: "#7a93a4" }}>{a.sub} · <span style={{ color: a.color, fontWeight: 600 }}>{a.status}</span></div>
                  <div style={{ fontSize: 10, color: "#aab8c2", marginTop: 1 }}>{a.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tip */}
      <div style={sCard}>
        <div style={{ padding: "14px 16px" }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(231,164,50,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#163244", marginBottom: 4 }}>Tip: descripción detallada</div>
          <div style={{ fontSize: 12, color: "#7a93a4", lineHeight: 1.55 }}>Los presupuestos con descripción clara y fecha de inicio tienen un 35% más de chances de ser aceptados.</div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProviderQuotesPage() {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [quotes,      setQuotes]      = useState([]);
  const [jobs,        setJobs]        = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [filter,      setFilter]      = useState("all");
  const [page,        setPage]        = useState(1);
  const PAGE_SIZE = 5;
  const [editing,     setEditing]     = useState(null);
  const [newModal,    setNewModal]    = useState(false);
  const [preselected, setPreselected] = useState(null);
  const [toast,       setToast]       = useState(null);

  const modalOpened = useRef(false);
  useEffect(() => {
    if (modalOpened.current) return;
    const state = location.state;
    if (state?.openQuoteModal && state?.requestId) {
      modalOpened.current = true;
      setPreselected({ requestId: state.requestId, label: state.label || null });
      setNewModal(true);
    }
  });

  useEffect(() => {
    setLoadingData(true); setLoadError("");
    const fetchQuotes = api.get("/matching/provider/quotes")
      .then(res => setQuotes(res.data.quotes || []))
      .catch(() => setLoadError("No se pudieron cargar los presupuestos."));
    // /api/provider/jobs nunca existió (404 desde 3928e1f): la agenda lateral
    // salía siempre vacía. La ruta real es la del calendario.
    const fetchJobs = api.get("/providers/calendar/jobs")
      .then(res => setJobs(res.data.jobs || res.data || []))
      .catch(() => setJobs([]));
    Promise.all([fetchQuotes, fetchJobs]).finally(() => setLoadingData(false));
  }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };
  const handleSaveEdit  = (updated) => { setQuotes(qs => qs.map(q => q.id === updated.id ? updated : q)); showToast("Presupuesto actualizado."); };
  const handleNewQuote  = (newQ)    => { setQuotes(qs => [newQ, ...qs]); showToast("¡Presupuesto enviado!"); };

  const FILTERS = [
    { id: "all",       label: "Todos",      count: quotes.length },
    { id: "submitted", label: "Enviados",   count: quotes.filter(q => q.status === "submitted").length },
    { id: "accepted",  label: "Aceptados",  count: quotes.filter(q => q.status === "accepted").length },
    { id: "rejected",  label: "Rechazados", count: quotes.filter(q => q.status === "rejected").length },
  ];

  const filtered = useMemo(() =>
    filter === "all" ? quotes : quotes.filter(q => q.status === filter),
    [quotes, filter]
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "28px 24px 60px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 290px", gap: 20, alignItems: "start" }}>

            {/* Columna izquierda */}
            <div>
              {/* Header */}
              <div style={{ marginBottom: 18 }}>
                <h1 style={{ margin: "0 0 4px", fontSize: 21, fontWeight: 800, color: "#163244" }}>Mis presupuestos</h1>
                <p style={{ margin: 0, color: "#7a93a4", fontSize: 13 }}>Seguí el estado de cada presupuesto enviado.</p>
              </div>

              {/* Filtros */}
              {!loadingData && !loadError && (
                <div style={{ display: "flex", gap: 5, marginBottom: 16, background: "#fff", borderRadius: 12, padding: 5, border: "1px solid rgba(0,0,0,0.07)" }}>
                  {FILTERS.map(f => (
                    <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }} style={{ flex: 1, padding: "8px 6px", borderRadius: 9, border: "none", background: filter === f.id ? "#163244" : "transparent", color: filter === f.id ? "#fff" : "#5a6e7f", fontFamily: font, fontWeight: filter === f.id ? 700 : 500, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, transition: "all 0.15s" }}>
                      {f.label}
                      <span style={{ borderRadius: 10, padding: "1px 6px", fontSize: 11, fontWeight: 800, background: filter === f.id ? "#E7A432" : "#e8eef2", color: filter === f.id ? "#163244" : "#5a6e7f" }}>{f.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Loading */}
              {loadingData && (
                <div style={{ textAlign: "center", padding: "60px", color: "#7a93a4" }}>
                  <span style={{ display: "inline-block", width: 26, height: 26, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} /><br/>
                  Cargando presupuestos...
                </div>
              )}

              {/* Error */}
              {loadError && !loadingData && (
                <div style={{ textAlign: "center", padding: "40px", background: "#fff", borderRadius: 14, color: "#c0392b", fontSize: 14 }}>⚠ {loadError}</div>
              )}

              {/* Empty */}
              {!loadingData && !loadError && filtered.length === 0 && (
                <div style={{ textAlign: "center", padding: "56px 20px", background: "#fff", borderRadius: 16, border: "2px dashed rgba(0,0,0,0.08)", color: "#aab8c2" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: "#163244" }}>
                    {filter === "all" ? "Todavía no enviaste presupuestos" : "No hay presupuestos en esta categoría"}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {filter === "all" ? "Aceptá solicitudes de presupuesto para poder enviar tu propuesta." : ""}
                  </div>
                </div>
              )}

              {/* Cards paginadas */}
              {!loadingData && !loadError && paginated.map(q => (
                <QuoteCard key={q.id} quote={q} onEdit={setEditing} onChat={id => navigate(`/chat/${id}`)} />
              ))}

              {/* Paginación */}
              {!loadingData && !loadError && totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 8 }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: page === 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", opacity: page === 1 ? 0.35 : 1 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setPage(p)}
                      style={{ width: 32, height: 32, borderRadius: 8, border: page === p ? "none" : "1px solid rgba(0,0,0,0.1)", background: page === p ? "#163244" : "#fff", color: page === p ? "#fff" : "#5a6e7f", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                      {p}
                    </button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: page === totalPages ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", opacity: page === totalPages ? 0.35 : 1 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                  <span style={{ fontSize: 12, color: "#7a93a4", marginLeft: 4 }}>
                    {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length}
                  </span>
                </div>
              )}
            </div>

            {/* Sidebar */}
            {!loadingData && !loadError && <Sidebar quotes={quotes} jobs={jobs} onNewQuote={() => setNewModal(true)} />}
          </div>
        </div>
      </div>

      {editing   && <EditModal quote={editing} onSave={handleSaveEdit} onClose={() => setEditing(null)} />}
      {newModal  && <NewQuoteModal onSave={handleNewQuote} onClose={() => { setNewModal(false); setPreselected(null); }} preselectedRequestId={preselected?.requestId || null} preselectedLabel={preselected?.label || null} />}

      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#163244", color: "#fff", padding: "13px 26px", borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", borderLeft: "4px solid #E7A432", whiteSpace: "nowrap", fontFamily: font }}>
          {toast}
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
      `}</style>
    </>
  );
}
